import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Loader2,
    Building,
    MapPin,
    Briefcase,
    CreditCard,
    CalendarRange,
    ArrowLeft,
    Check,
    Search,
    User,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    STATUS_REQUISITION_APPROVED,
    STATUS_DRAFT_CHANGE_REQUESTED,
    STATUS_PENDING_APPROVAL,
    REVIEW_APPROVED,
    REJECTED,
    POSITION_APPROVED,
} from '../../../constants';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PositionComments from './PositionComments';
import {
    Box,
    Container,
    Paper,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Button,
    TextField,
    Chip,
    Divider,
    Grid,
    LinearProgress,
    Alert,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';

import {
    useGetPreHiringPositionDetailsQuery,
    useUpdatePositionDetailsMutation,
    useAddPositionReviewCommentMutation,
    useGetJobTitlesQuery,
    useAddSkillsMutation,
    useGetLocationManagersQuery,
} from '../../../api/preHQ';
import AuditLog from '../../../components/common/AuditLog';
import AuditLogTable from '../../../components/common/AuditLog/AuditLogTable';
import BasicAttributesForm from '../approvedRequests/EditAndDraft/components/BasicAttributes';
import DynamicMetaFieldsForm from '../approvedRequests/EditAndDraft/components/DynamicMetaFieldsForm';
import { usePreCreatePositionMutation } from '../../../api/positions';
import PositionMenu from './Position/PositionMenu';
import PositionReview from './PositionReview';
import ActionButton from '../../../components/common/ActionButtonAndMenu/ActionButton';
import { hasPermission } from '../../../utils/permission';
import { useSelector } from 'react-redux';

const StyledContainer = styled(Container)(({ theme }) => ({
    padding: theme.spacing(3),
    maxWidth: '1200px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: 0,
    boxShadow: 'none',
    borderRadius: 0,
    borderTop: 'none',
    borderBottom: 'none',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
    // Improve readability for disabled fields
    '& .MuiInputLabel-root.Mui-disabled': {
        color: theme.palette.text.primary,
        opacity: 0.8,
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
        backgroundColor: theme.palette.grey[50],
        '& .MuiOutlinedInput-input': {
            color: theme.palette.text.primary,
            opacity: 0.9,
            '-webkit-text-fill-color': theme.palette.text.primary,
        },
    },
}));

const ViewModeTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputLabel-root': {
        color: theme.palette.text.primary,
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        '& .MuiOutlinedInput-input': {
            color: theme.palette.text.primary,
            fontWeight: 500,
            fontSize: '0.875rem',
            '-webkit-text-fill-color': theme.palette.text.primary,
        },
        '& fieldset': {
            border: 'none',
        },
    },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.5),
    // Keep the specific styling for mandatory chips from talentInsights/PositionSkills.jsx
    bgcolor: '#FFF4F0',
    color: '#ED7342',
    '&:hover': {
        bgcolor: '#FFF4F0',
        opacity: 0.8,
    },
    '& .MuiChip-deleteIcon': {
        color: '#ED7342',
        '&:hover': {
            color: '#ED7342',
            opacity: 0.8,
        },
    },
}));

// Quill Modules configuration - Keep this
const modules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
    ],
};

const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'bullet',
    'align',
    'link',
];

const PositionDetails = ({
    onPositionDetailsDataChange,
    onBasicAttributesDataChange,
    onCustomFieldsDataChange,
    handleMoveToDraft,
    validationErrors,
}) => {
    const errors = validationErrors?.positionDetails || {};
    const [searchParams] = useSearchParams();
    const currentUser = useSelector((state) => state.session.user);
    const navigate = useNavigate();
    const [preCreationHQPositionData, setPreCreationHQPositionData] = useState();

    const [preCreatePosition, { isLoading: isLoadingPreCreatePosition }] = usePreCreatePositionMutation();

    const [addPositionReviewComment, { isLoading: isAddingComment, error: addCommentError }] =
        useAddPositionReviewCommentMutation();

    const [updatePositionDetails, { isLoading: isUpdatingPositionDetails, error: updatePositionDetailsError }] =
        useUpdatePositionDetailsMutation();

    useEffect(() => {
        const fetchPreCreationData = async () => {
            try {
                const response = await preCreatePosition().unwrap();
                setPreCreationHQPositionData(response);
            } catch (error) {
                console.error('Error fetching pre-creation data:', error);
            }
        };
        fetchPreCreationData();
    }, [preCreatePosition]);

    const positionId = searchParams.get('positionId');
    const action = searchParams.get('action');
    const isDraft = searchParams.get('draft');
    const isEditable = action === 'edit';
    const isReview = action === 'review';
    const isView = action === 'view';

    if (isView && !hasPermission(currentUser.authorities, ['ROLE_BASE_PERMISSION_PRE_HQ_VIEW_POSITIONS']))
        return navigate('/access-denied');
    if (
        isEditable &&
        isDraft &&
        !hasPermission(currentUser.authorities, ['ROLE_BASE_PERMISSION_PRE_HQ_MOVE_TO_DRAFT_POSITIONS'])
    )
        return navigate('/access-denied');
    if (
        isEditable &&
        !isDraft &&
        !hasPermission(currentUser.authorities, ['ROLE_BASE_PERMISSION_PRE_HQ_MODIFY_POSITIONS'])
    )
        return navigate('/access-denied');
    if (isReview && !hasPermission(currentUser.authorities, ['ROLE_BASE_PERMISSION_PRE_HQ_REVIEW_POSITIONS']))
        return navigate('/access-denied');

    const [formData, setFormData] = useState({
        title: '',
        role: '',
        location: null,
        hiringManager: null,
        employmentType: '',
        minExperience: '',
        maxExperience: '',
        budgetLowerLimit: '',
        budgetMidLimit: '',
        budgetUpperLimit: '',
        mandatorySkills: [],
        optionalSkills: [],
        description: '',
    });
    const { data: position, isLoading, error, refetch } = useGetPreHiringPositionDetailsQuery(positionId);
    const [isSaving, setIsSaving] = useState(false);
    const originalData = useRef({});
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearch, setActiveSearch] = useState(null);

    const [jobTitleIsOpen, setJobTitleIsOpen] = useState(false);
    const [jobTitleSearch, setJobTitleSearch] = useState('');
    const jobTitleDropdownRef = useRef(null);

    const [roleIsOpen, setRoleIsOpen] = useState(false);
    const [roleSearch, setRoleSearch] = useState('');
    const roleDropdownRef = useRef(null);

    const [locationIsOpen, setLocationIsOpen] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');
    const locationDropdownRef = useRef(null);

    const [hiringManagerIsOpen, setHiringManagerIsOpen] = useState(false);
    const [hiringManagerSearch, setHiringManagerSearch] = useState('');
    const hiringManagerDropdownRef = useRef(null);

    const [basicAttributesData, setBasicAttributesData] = useState(null);
    const [customFieldsData, setCustomFieldsData] = useState(null);

    const mandatorySkillsDropdownRef = useRef(null);
    const commentsRef = useRef(null);

    useEffect(() => {
        if (onPositionDetailsDataChange) {
            onPositionDetailsDataChange(formData);
        }
    }, [formData, onPositionDetailsDataChange]);

    useEffect(() => {
        if (onBasicAttributesDataChange) {
            onBasicAttributesDataChange(basicAttributesData);
        }
    }, [basicAttributesData, onBasicAttributesDataChange]);

    useEffect(() => {
        if (onCustomFieldsDataChange) {
            onCustomFieldsDataChange(customFieldsData);
        }
    }, [customFieldsData, onCustomFieldsDataChange]);

    const employmentTypes = [
        { value: 'Full Time', label: 'Full Time' },
        { value: 'Part Time', label: 'Part Time' },
        { value: 'Contract', label: 'Contract' },
    ];

    // Keep the API calls, but their usage will change as dropdown is removed
    const { data: skillsData, isLoading: isLoadingSkills } = useGetJobTitlesQuery(
        { type: 'skill', query: searchTerm }, // Query can be empty or something default if not used directly for search suggestions
        { skip: false }, // Skip this query unless explicitly needed elsewhere.
    );

    const { data: jobTitleData, isLoading: isLoadingJobTitles } = useGetJobTitlesQuery(
        { type: 'title', query: jobTitleSearch },
        { skip: !jobTitleIsOpen && !jobTitleSearch },
    );
    const { data: roleData, isLoading: isLoadingRoles } = useGetJobTitlesQuery(
        { type: 'role', query: roleSearch },
        { skip: !roleIsOpen && !roleSearch },
    );
    const { data: locationManagerData, isLoading: isLoadingLocations } = useGetLocationManagersQuery({});

    const [addSkillsApi] = useAddSkillsMutation();

    useEffect(() => {
        if (positionId) refetch();
    }, [positionId, refetch]);

    useEffect(() => {
        if (position?.preHiringPosition) {
            const positionData = position.preHiringPosition || {};
            // Overwrite defaults with any data from the API
            setFormData((prev) => ({
                ...prev,
                ...positionData,
                description: positionData?.description ? decodeHtmlEntities(positionData.description) : '',
                hiringManager: { id: positionData?.hiringManager?.id, fullName: positionData?.hiringManager?.name },
                mandatorySkills: Array.isArray(positionData?.mandatorySkills)
                    ? positionData?.mandatorySkills
                    : positionData?.mandatorySkills
                      ? positionData?.mandatorySkills.split(',')?.map((s) => s.trim())
                      : [],
                optionalSkills: Array.isArray(positionData?.optionalSkills)
                    ? positionData?.optionalSkills
                    : positionData?.optionalSkills
                      ? positionData?.optionalSkills?.split(',')?.map((s) => s.trim())
                      : [],
            }));
            originalData.current = {
                ...positionData,
                description: positionData?.description ? decodeHtmlEntities(positionData.description) : '',
                mandatorySkills: Array.isArray(positionData?.mandatorySkills)
                    ? positionData?.mandatorySkills
                    : positionData?.mandatorySkills
                      ? positionData?.mandatorySkills?.split(',')?.map((s) => s.trim())
                      : [],
                optionalSkills: Array.isArray(positionData?.optionalSkills)
                    ? positionData?.optionalSkills
                    : positionData?.optionalSkills
                      ? positionData?.optionalSkills?.split(',')?.map((s) => s.trim())
                      : [],
            };
        }
    }, [position]);

    useEffect(() => {
        if (position?.preHiringPosition) {
            const positionData = position.preHiringPosition;
            if (positionData?.title) {
                setJobTitleSearch(positionData.title);
            }
            if (positionData?.role) {
                setRoleSearch(positionData.role);
            }
            if (positionData?.location?.name) {
                setLocationSearch(positionData.location.name);
            }
            if (positionData?.hiringManager?.name) {
                setHiringManagerSearch(positionData.hiringManager.name);
            }
        }
    }, [position]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (jobTitleDropdownRef.current && !jobTitleDropdownRef.current.contains(event.target)) {
                setJobTitleIsOpen(false);
            }
            if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
                setRoleIsOpen(false);
            }
            if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
                setLocationIsOpen(false);
            }
            if (hiringManagerDropdownRef.current && !hiringManagerDropdownRef.current.contains(event.target)) {
                setHiringManagerIsOpen(false);
            }
            if (mandatorySkillsDropdownRef.current && !mandatorySkillsDropdownRef.current.contains(event.target)) {
                setActiveSearch(null);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleJobTitleSelect = (title) => {
        setFormData((prev) => ({
            ...prev,
            title: title.trim(),
        }));
        setJobTitleIsOpen(false);
        setJobTitleSearch(title.trim());

        // Auto-populate department and skills based on job title
        if (jobTitleData?.results && jobTitleData.results[title]) {
            const associatedRoles = jobTitleData.results[title];
            if (associatedRoles.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    role: associatedRoles[0],
                }));
                setRoleSearch(associatedRoles[0]);

                // Set the mandatory skills from the associated role
                if (roleData?.results && roleData.results[associatedRoles[0]]) {
                    const skills = roleData.results[associatedRoles[0]];
                    setFormData((prev) => ({
                        ...prev,
                        mandatorySkills: skills,
                    }));
                }
            }
        }
    };

    const handleRoleSelect = (role) => {
        setFormData((prev) => ({
            ...prev,
            role: role.trim(),
        }));
        setRoleIsOpen(false);
        setRoleSearch(role.trim());

        // Set the mandatory skills from the selected role
        if (roleData?.results && roleData.results[role]) {
            const skills = roleData.results[role];
            setFormData((prev) => ({
                ...prev,
                mandatorySkills: skills,
            }));
        }
    };

    const handleLocationSelect = (location) => {
        setFormData((prev) => ({
            ...prev,
            location: { id: location.id, name: location.name.trim() },
        }));
        setLocationIsOpen(false);
        setLocationSearch(location.name.trim());
    };

    const handleHiringManagerSelect = (manager) => {
        setFormData((prev) => ({
            ...prev,
            hiringManager: { id: manager.id, fullName: manager.fullName.trim() },
        }));
        setHiringManagerIsOpen(false);
        setHiringManagerSearch(manager.fullName.trim());
    };

    // Common function to handle onBlur validation for dropdown fields
    const handleDropdownBlur = (
        searchValue,
        originalValue,
        dataList,
        dataKey,
        setSearchFunction,
        setIsOpenFunction,
    ) => {
        if (!searchValue || searchValue.trim() === '') {
            if (originalValue) {
                setSearchFunction(originalValue);
            }
        } else {
            const isValidOption = dataList?.some((item) => item[dataKey].toLowerCase() === searchValue.toLowerCase());
            if (!isValidOption && originalValue) {
                setSearchFunction(originalValue);
            }
        }
        setTimeout(() => {
            setIsOpenFunction(false);
        }, 100);
    };

    const handleJobTitleRoleBlur = (searchValue, originalValue, dataResults, setSearchFunction, setIsOpenFunction) => {
        if (!searchValue || searchValue.trim() === '') {
            if (originalValue) {
                setSearchFunction(originalValue);
            }
        } else {
            const isValidOption =
                dataResults && Object.keys(dataResults).some((key) => key.toLowerCase() === searchValue.toLowerCase());
            if (!isValidOption && originalValue) {
                setSearchFunction(originalValue);
            }
        }

        // Delay closing the dropdown to allow click events on list items to register.
        setTimeout(() => {
            setIsOpenFunction(false);
        }, 100);
    };

    const handleAddSkill = (skillObjectOrString, skillType) => {
        let skillName;
        // Determine the actual skill name from the input
        if (typeof skillObjectOrString === 'string') {
            skillName = skillObjectOrString;
        } else if (skillObjectOrString && typeof skillObjectOrString === 'object' && 'name' in skillObjectOrString) {
            skillName = skillObjectOrString.name;
        } else {
            console.warn('Unexpected skill input format:', skillObjectOrString);
            return;
        }

        // Now, trim and validate the extracted skillName
        if (typeof skillName !== 'string' || skillName.trim() === '') {
            return; // Prevent adding empty or non-string skills
        }

        if (skillType === 'mandatory') {
            if (formData.mandatorySkills.length >= 7) {
                toast.error('You can only add a maximum of 7 mandatory skills.');
                return;
            }

            setFormData((prev) => {
                const existingIndex = prev.mandatorySkills.findIndex(
                    (s) => s.toLowerCase() === skillName.toLowerCase(),
                );
                const filteredSkills =
                    existingIndex >= 0
                        ? [
                              ...prev.mandatorySkills.slice(0, existingIndex),
                              ...prev.mandatorySkills.slice(existingIndex + 1),
                          ]
                        : [...prev.mandatorySkills];
                const updatedSkills = [...filteredSkills, skillName.trim()];

                const skillsPayload = {
                    skills: updatedSkills,
                };
                addSkillsApi(skillsPayload);
                return {
                    ...prev,
                    mandatorySkills: updatedSkills,
                };
            });
        } else if (skillType === 'optional') {
            setFormData((prev) => {
                const existingIndex = prev.optionalSkills.findIndex((s) => s.toLowerCase() === skillName.toLowerCase());
                const filteredSkills =
                    existingIndex >= 0
                        ? [
                              ...prev.optionalSkills.slice(0, existingIndex),
                              ...prev.optionalSkills.slice(existingIndex + 1),
                          ]
                        : [...prev.optionalSkills];
                return {
                    ...prev,
                    optionalSkills: [...filteredSkills, skillName.trim()],
                };
            });
        }
        setSearchTerm('');
        setActiveSearch(null);
    };

    const handleDeleteSkill = (skillToDelete, skillType) => () => {
        if (skillType === 'mandatorySkills') {
            setFormData((prev) => {
                const updatedSkills = prev.mandatorySkills.filter((skill) => skill !== skillToDelete);
                const skillsPayload = {
                    skills: updatedSkills,
                };
                addSkillsApi(skillsPayload); // Call API for mandatory skills
                return {
                    ...prev,
                    mandatorySkills: updatedSkills,
                };
            });
        } else if (skillType === 'optionalSkills') {
            setFormData((prev) => ({
                ...prev,
                optionalSkills: prev.optionalSkills.filter((skill) => skill !== skillToDelete),
            }));
        }
    };

    const hasValueChanged = (key, newValue, originalValue) => {
        if (key === 'location') {
            const newLocationName = typeof newValue === 'object' && newValue !== null ? newValue.name : undefined;

            const originalLocationName =
                typeof originalValue === 'object' && originalValue !== null ? originalValue.name : undefined;

            return newLocationName !== originalLocationName;
        }

        if (key === 'hiringManager') {
            const newManagerName = typeof newValue === 'object' && newValue !== null ? newValue.fullName : undefined;

            const originalManagerName =
                typeof originalValue === 'object' && originalValue !== null ? originalValue.fullName : undefined;

            return newManagerName !== originalManagerName;
        }

        if (typeof originalValue === 'number') {
            return Number(newValue) !== originalValue;
        }

        if (key === 'mandatorySkills' || key === 'optionalSkills') {
            const newSkills = Array.isArray(newValue) ? newValue.join(',') : newValue;
            const originalSkills = Array.isArray(originalValue) ? originalValue.join(',') : originalValue;
            return newSkills !== originalSkills;
        }

        return newValue !== originalValue;
    };

    const handleSave = async () => {
        if (!positionId) {
            toast.error('Position ID is missing.');
            return;
        }
        if (formData.minExperience > 99 || formData.maxExperience > 99) {
            toast.error('Experience cannot be more than 99 years');
            return;
        }

        if (formData.minExperience > formData.maxExperience) {
            toast.error('Minimum experience cannot be more than maximum experience');
            return;
        }

        try {
            setIsSaving(true);

            const changedFields = {};
            let hasChanges = false;

            Object.keys(formData).forEach((key) => {
                if (key in originalData.current) {
                    if (hasValueChanged(key, formData[key], originalData.current[key])) {
                        if (key === 'mandatorySkills' || key === 'optionalSkills') {
                            changedFields[key] = Array.isArray(formData[key]) ? formData[key].join(',') : formData[key];
                        } else if (key === 'hiringManager' || key === 'location') {
                            changedFields[key] = formData[key].id;
                        } else {
                            changedFields[key] = formData[key];
                        }
                        hasChanges = true;
                    }
                }
            });

            if (basicAttributesData || customFieldsData) {
                hasChanges = true;
                changedFields['internalHqProperties'] = {
                    basicAttributesData,
                    customFieldsData,
                };
            }

            if (!hasChanges) {
                toast('The position details remain unchanged.', {
                    duration: 3000,
                });
                return;
            }

            const result = await updatePositionDetails({ positionId, payload: changedFields }).unwrap();

            if (result) {
                originalData.current = { ...formData };
                toast.success('The position details have been updated.');
                setTimeout(() => {
                    navigate('/client/positions');
                }, 1500);
            }
        } catch (error) {
            console.error('Error saving position:', error);
            toast.error('There was a problem updating the position details.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleBack = () => {
        navigate('/client/positions');
    };

    // Function to decode HTML entities
    const decodeHtmlEntities = (html) => {
        if (!html) return '';
        const textarea = document.createElement('textarea');
        textarea.innerHTML = html;
        return textarea.value;
    };

    const handleCommentAction = async (action) => {
        if (!positionId) {
            toast.error('Position ID is missing.');
            return;
        }

        try {
            let status;
            let successMessage;

            if (action === POSITION_APPROVED) {
                status = STATUS_REQUISITION_APPROVED;
                successMessage = 'The position has been moved to the Approved stage';
            } else if (action === REJECTED) {
                status = STATUS_DRAFT_CHANGE_REQUESTED;
                successMessage = 'The position has been moved to the Change Requested stage';
            } else if (action === REVIEW_APPROVED) {
                status = STATUS_PENDING_APPROVAL;
                successMessage = 'The position has been moved to the Review stage';
            }

            if (status) {
                const result = await updatePositionDetails({ positionId, payload: { status } }).unwrap();
                if (result) {
                    toast.success(successMessage);
                    // Refetch the position data to get updated status
                    refetch();
                    setTimeout(() => {
                        navigate('/client/positions');
                    }, 1500);
                }
            }
        } catch (error) {
            console.error('Error updating position status:', error);
            toast.error('There was a problem updating the position status.');
        }
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80vh',
                    py: 3,
                }}
            >
                <CircularProgress size={60} sx={{ color: 'orange.500', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    Loading position details...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80vh',
                    py: 3,
                }}
            >
                <Alert severity="error" sx={{ mb: 2 }}>
                    Error loading position details
                </Alert>
                <Button variant="outlined" onClick={() => refetch()}>
                    Try Again
                </Button>
            </Box>
        );
    }

    if (!formData.id) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80vh',
                    py: 3,
                }}
            >
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Position not found
                </Typography>
                <Button variant="outlined" onClick={handleBack}>
                    Back to Positions
                </Button>
            </Box>
        );
    }

    return (
        <StyledContainer maxWidth="xl">
            {/* Navigation Section */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    startIcon={<ChevronLeftIcon />}
                    variant="text"
                    color="secondary"
                    onClick={() => navigate('/client/positions')}
                >
                    Back
                </Button>
                {isView && (
                    <PositionMenu
                        Component={ActionButton}
                        row={position?.preHiringPosition || {}}
                        handleAction={(actionType) => {
                            if (actionType === 'review') {
                                // Scroll to comments section
                                setTimeout(() => {
                                    commentsRef.current?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start',
                                    });
                                }, 100);
                            }
                        }}
                    />
                )}
            </Box>

            <Typography variant="h3" component="h1" sx={{ mb: 4, fontWeight: 'bold', color: 'text.primary' }}>
                Position Details
            </Typography>

            <Box sx={{ bgcolor: 'white', borderRadius: 1, overflow: 'hidden' }}>
                <StyledCard sx={{ borderBottom: '1px solid', borderColor: 'primary.100' }}>
                    <CardHeader
                        title="Overview"
                        variant="h5"
                        subheader="High-level details about the position"
                        action={
                            <Chip
                                label={position?.preHiringPosition?.status || '-'}
                                size="small"
                                sx={{
                                    bgcolor: 'primary.50',
                                    color: 'primary.main',
                                    border: '1px solid',
                                    borderColor: 'primary.200',
                                    fontWeight: 600,
                                    mt: 1.5,
                                    mr: 2,
                                }}
                            />
                        }
                    />
                    <CardContent>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Building size={16} />
                                    <Typography variant="h6">{formData.role || '-'}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MapPin size={16} />
                                    <Typography variant="h6">{formData.location?.name || '-'}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Briefcase size={16} />
                                    <Typography variant="h6">{formData.employmentType || '-'}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarRange size={16} />
                                    <Typography variant="h6">
                                        {formData.minExperience && formData.maxExperience
                                            ? `${formData.minExperience} - ${formData.maxExperience} years`
                                            : 'Experience not specified'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <User size={16} />
                                    <Typography variant="h6">{formData.hiringManager?.fullName || '-'}</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </StyledCard>

                <StyledCard>
                    <CardHeader
                        title="Position Details"
                        subheader="Core information, skills, and budget for the position"
                    />
                    <CardContent>
                        {/* Basic Details Section */}
                        <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 500 }}>
                            Basic Details
                        </Typography>
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={6}>
                                {/* Job Title with Custom Dropdown */}
                                <Box ref={jobTitleDropdownRef} sx={{ position: 'relative' }}>
                                    {isEditable ? (
                                        <StyledTextField
                                            fullWidth
                                            label="Position Title"
                                            placeholder="Search job titles..."
                                            value={jobTitleSearch}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setJobTitleSearch(value.trim());
                                                setFormData((prev) => ({ ...prev, title: value.trim() }));
                                            }}
                                            onFocus={() => {
                                                setJobTitleIsOpen(true);
                                                if (formData.title) {
                                                    setJobTitleSearch('');
                                                }
                                            }}
                                            onBlur={() => {
                                                handleJobTitleRoleBlur(
                                                    jobTitleSearch,
                                                    formData.title,
                                                    jobTitleData?.results,
                                                    setJobTitleSearch,
                                                    setJobTitleIsOpen,
                                                );
                                            }}
                                            size="small"
                                            error={!!errors?.title}
                                            helperText={errors?.title?.[0]}
                                            InputProps={{
                                                endAdornment: isLoadingJobTitles ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <Search sx={{ color: 'grey.400' }} />
                                                ),
                                            }}
                                        />
                                    ) : (
                                        <ViewModeTextField
                                            fullWidth
                                            label="Position Title"
                                            value={formData.title || '-'}
                                            size="small"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    )}

                                    {jobTitleIsOpen && (
                                        <Paper
                                            sx={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                zIndex: 1000,
                                                mt: 1,
                                                maxHeight: 240,
                                                overflow: 'auto',
                                                boxShadow: 3,
                                            }}
                                        >
                                            <List dense>
                                                {jobTitleData?.results &&
                                                Object.keys(jobTitleData.results).length > 0 ? (
                                                    Object.keys(jobTitleData.results)
                                                        .filter((title) =>
                                                            title.toLowerCase().includes(jobTitleSearch.toLowerCase()),
                                                        )
                                                        .map((title, index) => (
                                                            <ListItem
                                                                button
                                                                key={index}
                                                                onMouseDown={() => handleJobTitleSelect(title)}
                                                            >
                                                                <ListItemText primary={title} />
                                                                {formData.title === title && (
                                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                                        <Check fontSize="small" color="primary" />
                                                                    </ListItemIcon>
                                                                )}
                                                            </ListItem>
                                                        ))
                                                ) : jobTitleSearch ? (
                                                    <ListItem
                                                        button
                                                        onClick={() => handleJobTitleSelect(jobTitleSearch)}
                                                    >
                                                        <ListItemText primary={jobTitleSearch} />
                                                    </ListItem>
                                                ) : (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="No job titles found"
                                                            sx={{ color: 'text.secondary' }}
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </Paper>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {/* Role with Custom Dropdown */}
                                <Box ref={roleDropdownRef} sx={{ position: 'relative' }}>
                                    {isEditable ? (
                                        <StyledTextField
                                            fullWidth
                                            label="Role"
                                            placeholder="Search roles..."
                                            value={roleSearch}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setRoleSearch(value.trim());
                                                setFormData((prev) => ({ ...prev, role: value.trim() }));
                                            }}
                                            onFocus={() => {
                                                setRoleIsOpen(true);
                                                if (formData.role) {
                                                    setRoleSearch('');
                                                }
                                            }}
                                            onBlur={() => {
                                                handleJobTitleRoleBlur(
                                                    roleSearch,
                                                    formData.role,
                                                    roleData?.results,
                                                    setRoleSearch,
                                                    setRoleIsOpen,
                                                );
                                            }}
                                            size="small"
                                            error={!!errors?.role}
                                            helperText={errors?.role?.[0]}
                                            InputProps={{
                                                endAdornment: isLoadingRoles ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <Search sx={{ color: 'grey.400' }} />
                                                ),
                                            }}
                                        />
                                    ) : (
                                        <ViewModeTextField
                                            fullWidth
                                            label="Role"
                                            value={formData.role || '-'}
                                            size="small"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    )}

                                    {roleIsOpen && (
                                        <Paper
                                            sx={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                zIndex: 1000,
                                                mt: 1,
                                                maxHeight: 240,
                                                overflow: 'auto',
                                                boxShadow: 3,
                                            }}
                                        >
                                            <List dense>
                                                {roleData?.results && Object.keys(roleData.results).length > 0 ? (
                                                    Object.keys(roleData.results)
                                                        .filter((role) =>
                                                            role.toLowerCase().includes(roleSearch.toLowerCase()),
                                                        )
                                                        .map((role, index) => (
                                                            <ListItem
                                                                button
                                                                key={index}
                                                                onMouseDown={() => handleRoleSelect(role)}
                                                            >
                                                                <ListItemText primary={role} />
                                                                {formData.role === role && (
                                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                                        <Check fontSize="small" color="primary" />
                                                                    </ListItemIcon>
                                                                )}
                                                            </ListItem>
                                                        ))
                                                ) : (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="No roles found"
                                                            sx={{ color: 'text.secondary' }}
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </Paper>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {isEditable ? (
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        disabled={!isEditable}
                                        error={!!errors?.employmentType}
                                    >
                                        <InputLabel>Employment Type</InputLabel>
                                        <Select
                                            name="employmentType"
                                            value={formData.employmentType || 'Employment'}
                                            label="Employment Type"
                                            onChange={handleInputChange}
                                        >
                                            {employmentTypes.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors?.employmentType && (
                                            <FormHelperText>{errors.employmentType[0]}</FormHelperText>
                                        )}
                                    </FormControl>
                                ) : (
                                    <ViewModeTextField
                                        fullWidth
                                        label="Employment Type"
                                        name="employmentType"
                                        value={
                                            employmentTypes.find((option) => option.value === formData.employmentType)
                                                ?.label ||
                                            formData.employmentType ||
                                            '-'
                                        }
                                        disabled
                                        size="small"
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {/* Location with Custom Dropdown */}
                                <Box ref={locationDropdownRef} sx={{ position: 'relative' }}>
                                    {isEditable ? (
                                        <StyledTextField
                                            fullWidth
                                            label="Location"
                                            placeholder="Search locations..."
                                            value={locationSearch}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocationSearch(value.trim());
                                            }}
                                            onFocus={() => {
                                                setLocationIsOpen(true);
                                                if (formData.location?.name) {
                                                    setLocationSearch('');
                                                }
                                            }}
                                            onBlur={() => {
                                                handleDropdownBlur(
                                                    locationSearch,
                                                    formData.location?.name,
                                                    locationManagerData?.locationsList,
                                                    'name',
                                                    setLocationSearch,
                                                    setLocationIsOpen,
                                                );
                                            }}
                                            size="small"
                                            error={!!errors?.location}
                                            helperText={errors?.location?.[0]}
                                            InputProps={{
                                                endAdornment: isLoadingLocations ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <Search sx={{ color: 'grey.400' }} />
                                                ),
                                            }}
                                        />
                                    ) : (
                                        <ViewModeTextField
                                            fullWidth
                                            label="Location"
                                            value={formData.location?.name || '-'}
                                            size="small"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    )}

                                    {locationIsOpen && (
                                        <Paper
                                            sx={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                zIndex: 1000,
                                                mt: 1,
                                                maxHeight: 240,
                                                overflow: 'auto',
                                                boxShadow: 3,
                                            }}
                                        >
                                            <List dense>
                                                {locationManagerData?.locationsList &&
                                                locationManagerData.locationsList.length > 0 ? (
                                                    locationManagerData.locationsList
                                                        .filter((location) =>
                                                            location.name
                                                                .toLowerCase()
                                                                .includes(locationSearch.toLowerCase()),
                                                        )
                                                        .map((location, index) => (
                                                            <ListItem
                                                                button
                                                                key={index}
                                                                onMouseDown={() => handleLocationSelect(location)}
                                                            >
                                                                <ListItemText primary={location.name} />
                                                                {formData.location?.name === location.name && (
                                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                                        <Check fontSize="small" color="primary" />
                                                                    </ListItemIcon>
                                                                )}
                                                            </ListItem>
                                                        ))
                                                ) : (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="No locations found"
                                                            sx={{ color: 'text.secondary' }}
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </Paper>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {/* Hiring Manager with Custom Dropdown */}
                                <Box ref={hiringManagerDropdownRef} sx={{ position: 'relative' }}>
                                    {isEditable ? (
                                        <StyledTextField
                                            fullWidth
                                            label="Hiring Manager"
                                            placeholder="Search hiring managers..."
                                            value={hiringManagerSearch}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setHiringManagerSearch(value.trim());
                                            }}
                                            onFocus={() => {
                                                setHiringManagerIsOpen(true);
                                                if (formData.hiringManager?.fullName) {
                                                    setHiringManagerSearch('');
                                                }
                                            }}
                                            onBlur={() => {
                                                handleDropdownBlur(
                                                    hiringManagerSearch,
                                                    formData.hiringManager?.fullName,
                                                    locationManagerData?.employeeList,
                                                    'fullName',
                                                    setHiringManagerSearch,
                                                    setHiringManagerIsOpen,
                                                );
                                            }}
                                            size="small"
                                            error={!!errors?.hiringManager}
                                            helperText={errors?.hiringManager?.[0]}
                                            InputProps={{
                                                endAdornment: isLoadingLocations ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <Search sx={{ color: 'grey.400' }} />
                                                ),
                                            }}
                                        />
                                    ) : (
                                        <ViewModeTextField
                                            fullWidth
                                            label="Hiring Manager"
                                            value={formData.hiringManager?.fullName || '-'}
                                            size="small"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    )}

                                    {hiringManagerIsOpen && (
                                        <Paper
                                            sx={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                zIndex: 1000,
                                                mt: 1,
                                                maxHeight: 240,
                                                overflow: 'auto',
                                                boxShadow: 3,
                                            }}
                                        >
                                            <List dense>
                                                {locationManagerData?.employeeList &&
                                                locationManagerData.employeeList.length > 0 ? (
                                                    locationManagerData.employeeList
                                                        .filter((manager) =>
                                                            manager.fullName
                                                                .toLowerCase()
                                                                .includes(hiringManagerSearch.toLowerCase()),
                                                        )
                                                        .map((manager, index) => (
                                                            <ListItem
                                                                button
                                                                key={index}
                                                                onMouseDown={() => handleHiringManagerSelect(manager)}
                                                            >
                                                                <ListItemText primary={manager.fullName} />
                                                                {formData.hiringManager?.fullName ===
                                                                    manager.fullName && (
                                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                                        <Check fontSize="small" color="primary" />
                                                                    </ListItemIcon>
                                                                )}
                                                            </ListItem>
                                                        ))
                                                ) : hiringManagerSearch ? (
                                                    <ListItem
                                                        button
                                                        onClick={() =>
                                                            handleHiringManagerSelect({
                                                                id: hiringManagerSearch,
                                                                fullName: hiringManagerSearch,
                                                            })
                                                        }
                                                    >
                                                        <ListItemText primary={hiringManagerSearch} />
                                                    </ListItem>
                                                ) : (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="No hiring managers found"
                                                            sx={{ color: 'text.secondary' }}
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </Paper>
                                    )}
                                </Box>
                            </Grid>
                            {isDraft && (
                                <Grid item xs={12} md={6}>
                                    {isEditable ? (
                                        <StyledTextField
                                            fullWidth
                                            label="Position Code"
                                            name="positionCode"
                                            value={formData.positionCode || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter position code"
                                            size="small"
                                            error={!!errors?.positionCode}
                                            helperText={errors?.positionCode?.[0]}
                                        />
                                    ) : (
                                        <ViewModeTextField
                                            fullWidth
                                            label="Position Code"
                                            value={formData.positionCode || '-'}
                                            size="small"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    )}
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Experience Range (years)
                                </Typography>
                                {isEditable ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <StyledTextField
                                            label="Min"
                                            name="minExperience"
                                            type="number"
                                            value={formData.minExperience || ''}
                                            onChange={handleInputChange}
                                            sx={{ width: 80 }}
                                            inputProps={{ min: 0 }}
                                            error={!!errors?.minExperience}
                                            helperText={errors?.minExperience?.[0]}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            to
                                        </Typography>
                                        <StyledTextField
                                            label="Max"
                                            name="maxExperience"
                                            type="number"
                                            value={formData.maxExperience || ''}
                                            onChange={handleInputChange}
                                            sx={{ width: 80 }}
                                            inputProps={{ min: 0 }}
                                            error={!!errors?.maxExperience}
                                            helperText={errors?.maxExperience?.[0]}
                                        />
                                    </Box>
                                ) : (
                                    <Typography variant="body1" sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                                        {formData.minExperience && formData.maxExperience
                                            ? `${formData.minExperience} - ${formData.maxExperience} years`
                                            : 'Experience range not specified'}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>

                        {/* Skills Required Section */}
                        <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 500 }}>
                            Skills Required
                        </Typography>
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={6} sx={{ overflow: 'visible' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Mandatory Skills
                                </Typography>
                                {errors?.mandatorySkills && (
                                    <FormHelperText error sx={{ mb: 1 }}>
                                        {errors.mandatorySkills[0]}
                                    </FormHelperText>
                                )}
                                {isEditable ? (
                                    <Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                            {formData.mandatorySkills.map((skill, index) => (
                                                <StyledChip
                                                    key={index}
                                                    label={skill.trim()}
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                    onDelete={handleDeleteSkill(skill, 'mandatorySkills')}
                                                />
                                            ))}
                                        </Box>
                                        <Typography
                                            component="button"
                                            onClick={() => setActiveSearch('mandatory')}
                                            sx={{
                                                fontSize: '0.875rem',
                                                color: '#ED7342',
                                                '&:hover': {
                                                    color: '#ED7342',
                                                    opacity: 0.9,
                                                },
                                                cursor:
                                                    formData.mandatorySkills.length >= 7 ? 'not-allowed' : 'pointer',
                                                opacity: formData.mandatorySkills.length >= 7 ? 0.5 : 1,
                                                border: 'none',
                                                bgcolor: 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                            disabled={formData.mandatorySkills.length >= 7}
                                        >
                                            <span>+</span>
                                            Add Skills
                                        </Typography>
                                        {activeSearch === 'mandatory' && (
                                            <Box ref={mandatorySkillsDropdownRef} sx={{ position: 'relative', mt: 1 }}>
                                                <StyledTextField
                                                    fullWidth
                                                    label="Search skills..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    InputProps={{
                                                        startAdornment: <Search size={16} />,
                                                    }}
                                                    size="small"
                                                />

                                                {isLoadingSkills ? (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            zIndex: 10,
                                                            width: '100%',
                                                            bgcolor: 'white',
                                                            boxShadow: 3,
                                                            borderRadius: 1,
                                                            mt: 0.5,
                                                            border: 1,
                                                            borderColor: 'divider',
                                                            p: 2,
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                            <CircularProgress size={16} sx={{ color: '#ED7342' }} />
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    searchTerm && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                zIndex: 10,
                                                                width: '100%',
                                                                bgcolor: 'white',
                                                                boxShadow: 3,
                                                                borderRadius: 1,
                                                                mt: 0.5,
                                                                border: 1,
                                                                borderColor: 'divider',
                                                                maxHeight: 192,
                                                                overflow: 'auto',
                                                            }}
                                                        >
                                                            {skillsData?.results?.length > 0 ? (
                                                                skillsData.results.map((skill, index) => (
                                                                    <MenuItem
                                                                        key={index}
                                                                        onClick={() =>
                                                                            handleAddSkill(skill, 'mandatory')
                                                                        }
                                                                        sx={{
                                                                            py: 1,
                                                                            px: 2,
                                                                            '&:hover': {
                                                                                bgcolor: 'action.hover',
                                                                            },
                                                                        }}
                                                                    >
                                                                        {skill}
                                                                    </MenuItem>
                                                                ))
                                                            ) : (
                                                                <MenuItem disabled>No skills found</MenuItem>
                                                            )}
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        )}
                                        {formData.mandatorySkills.length >= 7 && ( // Keep max 7 limit warning
                                            <Alert severity="warning" sx={{ mt: 2 }}>
                                                You can only add a maximum of 7 mandatory skills.
                                            </Alert>
                                        )}
                                    </Box>
                                ) : (
                                    <Box>
                                        {formData.mandatorySkills && formData.mandatorySkills.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {formData.mandatorySkills.map((skill, index) => (
                                                    <StyledChip
                                                        key={index}
                                                        label={skill.trim()}
                                                        color="primary"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography
                                                variant="body1"
                                                sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}
                                            >
                                                No mandatory skills specified
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6} sx={{ overflow: 'visible' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Optional Skills
                                </Typography>
                                {isEditable ? (
                                    <Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                            {formData.optionalSkills.map((skill, index) => (
                                                <StyledChip
                                                    key={index}
                                                    label={skill.trim()}
                                                    color="default"
                                                    variant="outlined"
                                                    size="small"
                                                    onDelete={handleDeleteSkill(skill, 'optionalSkills')}
                                                />
                                            ))}
                                        </Box>
                                        <Typography
                                            component="button"
                                            onClick={() => setActiveSearch('optional')}
                                            sx={{
                                                fontSize: '0.875rem',
                                                color: '#ED7342',
                                                '&:hover': {
                                                    color: '#ED7342',
                                                    opacity: 0.9,
                                                },
                                                cursor: 'pointer',
                                                border: 'none',
                                                bgcolor: 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <span>+</span>
                                            Add Skills
                                        </Typography>
                                        {activeSearch === 'optional' && (
                                            <Box sx={{ position: 'relative', mt: 1 }}>
                                                <StyledTextField
                                                    fullWidth
                                                    label="Search skills..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    InputProps={{
                                                        startAdornment: <Search size={16} />,
                                                    }}
                                                    size="small"
                                                />
                                                {isLoadingSkills ? (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            zIndex: 10,
                                                            width: '100%',
                                                            bgcolor: 'white',
                                                            boxShadow: 3,
                                                            borderRadius: 1,
                                                            mt: 0.5,
                                                            border: 1,
                                                            borderColor: 'divider',
                                                            p: 2,
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                            <CircularProgress size={16} sx={{ color: '#ED7342' }} />
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    searchTerm && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                zIndex: 10,
                                                                width: '100%',
                                                                bgcolor: 'white',
                                                                boxShadow: 3,
                                                                borderRadius: 1,
                                                                mt: 0.5,
                                                                border: 1,
                                                                borderColor: 'divider',
                                                                maxHeight: 192,
                                                                overflow: 'auto',
                                                            }}
                                                        >
                                                            {skillsData?.results?.length > 0 ? (
                                                                skillsData.results.map((skill, index) => (
                                                                    <MenuItem
                                                                        key={index}
                                                                        onClick={() =>
                                                                            handleAddSkill(skill, 'optional')
                                                                        }
                                                                        sx={{
                                                                            py: 1,
                                                                            px: 2,
                                                                            '&:hover': {
                                                                                bgcolor: 'action.hover',
                                                                            },
                                                                        }}
                                                                    >
                                                                        {skill}
                                                                    </MenuItem>
                                                                ))
                                                            ) : (
                                                                <MenuItem disabled>No skills found</MenuItem>
                                                            )}
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Box>
                                        {formData.optionalSkills && formData.optionalSkills.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {formData.optionalSkills.map((skill, index) => (
                                                    <StyledChip
                                                        key={index}
                                                        label={skill.trim()}
                                                        color="default"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography
                                                variant="body1"
                                                sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}
                                            >
                                                No optional skills specified
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>

                        {/* Budget Information Section */}
                        <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 500 }}>
                            Budget Information
                        </Typography>
                        {isEditable ? (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <CreditCard size={16} />
                                        <Typography variant="subtitle2">Budget Lower Limit</Typography>
                                    </Box>
                                    <StyledTextField
                                        fullWidth
                                        type="number"
                                        name="budgetLowerLimit"
                                        value={formData.budgetLowerLimit || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter amount"
                                        inputProps={{ min: 0 }}
                                        error={!!errors?.budgetLowerLimit}
                                        helperText={errors?.budgetLowerLimit?.[0]}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <CreditCard size={16} />
                                        <Typography variant="subtitle2">Budget Mid Limit</Typography>
                                    </Box>
                                    <StyledTextField
                                        fullWidth
                                        type="number"
                                        name="budgetMidLimit"
                                        value={formData.budgetMidLimit || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter amount"
                                        inputProps={{ min: 0 }}
                                        error={!!errors?.budgetMidLimit}
                                        helperText={errors?.budgetMidLimit?.[0]}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <CreditCard size={16} />
                                        <Typography variant="subtitle2">Budget Upper Limit</Typography>
                                    </Box>
                                    <StyledTextField
                                        fullWidth
                                        type="number"
                                        name="budgetUpperLimit"
                                        value={formData.budgetUpperLimit || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter amount"
                                        inputProps={{ min: 0 }}
                                        error={!!errors?.budgetUpperLimit}
                                        helperText={errors?.budgetUpperLimit?.[0]}
                                    />
                                </Grid>
                            </Grid>
                        ) : (
                            <Box>
                                <Grid container spacing={3} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <CreditCard size={16} />
                                            <Typography variant="subtitle2">Budget Lower Limit</Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                                            {formData.budgetLowerLimit
                                                ? formData.budgetLowerLimit.toLocaleString()
                                                : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <CreditCard size={16} />
                                            <Typography variant="subtitle2">Budget Mid Limit</Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                                            {formData.budgetMidLimit ? formData.budgetMidLimit.toLocaleString() : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <CreditCard size={16} />
                                            <Typography variant="subtitle2">Budget Upper Limit</Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                                            {formData.budgetUpperLimit
                                                ? formData.budgetUpperLimit.toLocaleString()
                                                : '-'}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {formData.budgetLowerLimit && formData.budgetUpperLimit ? (
                                    <Box sx={{ mt: 3 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 1,
                                            }}
                                        >
                                            <Chip
                                                label="Salary Range"
                                                color="primary"
                                                size="small"
                                                variant="outlined"
                                            />
                                            <Typography variant="body2" color="primary">
                                                {formData.budgetLowerLimit.toLocaleString()} -{' '}
                                                {formData.budgetUpperLimit.toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={100}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>
                                ) : (
                                    <></>
                                )}
                                <Box mt={3}>
                                    <AuditLog
                                        data={[
                                            {
                                                title: 'Audit Logs',
                                                logs: formData?.auditCommentsList || [],
                                                Component: AuditLogTable,
                                            },
                                        ]}
                                    />
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </StyledCard>

                <StyledCard sx={{ borderTop: 'none' }}>
                    <CardHeader title="Job Description" subheader="Detailed information about the role" />
                    <CardContent>
                        {isEditable ? (
                            <>
                                <ReactQuill
                                    theme="snow"
                                    value={formData.description || ''}
                                    onChange={(content) =>
                                        handleInputChange({ target: { name: 'description', value: content } })
                                    }
                                    modules={modules}
                                    formats={formats}
                                    placeholder="Enter detailed job description..."
                                />
                                {errors?.description && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <InfoIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                                        <FormHelperText error sx={{ fontSize: '1rem' }}>
                                            {errors.description[0]}
                                        </FormHelperText>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                                {formData.description ? (
                                    <div
                                        dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(formData.description) }}
                                    />
                                ) : (
                                    <Typography variant="body1">No job description available</Typography>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </StyledCard>
                <BasicAttributesForm
                    preData={position?.preHiringPosition}
                    data={basicAttributesData}
                    isLoading={isLoading || isLoadingPreCreatePosition}
                    preCreationHQPositionData={preCreationHQPositionData}
                    onDataChange={setBasicAttributesData}
                    errors={isDraft ? validationErrors.basicAttributes : {}}
                />
                <DynamicMetaFieldsForm
                    preData={position?.preHiringPosition}
                    data={customFieldsData}
                    isLoading={isLoading || isLoadingPreCreatePosition}
                    preCreationHQPositionData={preCreationHQPositionData}
                    onDataChange={setCustomFieldsData}
                    errors={isDraft ? validationErrors.customFields : {}}
                />
                <PositionReview reviewPolicy={position?.preHiringPosition?.reviewPolicy} />
                <div ref={commentsRef}>
                    <PositionComments
                        comments={formData.comments}
                        status={formData.status}
                        isReview={isReview}
                        isView={isView}
                        isDraft={isDraft}
                        addComment={addPositionReviewComment}
                        isAddingComment={isAddingComment}
                        addCommentError={addCommentError}
                        positionId={positionId || ''}
                        data={position?.preHiringPosition}
                        refetch={refetch}
                        handleCommentAction={handleCommentAction}
                    />
                </div>

                {isEditable && !isDraft && (
                    <Box sx={{ mt: 4, px: '24px', display: 'flex', justifyContent: 'space-between' }}>
                        <Button variant="outlined" startIcon={<ArrowLeft />} onClick={handleBack}>
                            Back to Positions
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={isSaving ? <CircularProgress size={16} /> : <Check />}
                            onClick={handleSave}
                            disabled={isSaving}
                            sx={{
                                bgcolor: 'orange.500',
                                '&:hover': { bgcolor: 'orange.600' },
                            }}
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                )}
                {isEditable && isDraft && (
                    <Box sx={{ mt: 4, px: '24px', display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => navigate('/client/positions')}
                        >
                            Back
                        </Button>
                        <Box>
                            <Button
                                sx={{ marginRight: '24px' }}
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                            <Button variant="contained" color="primary" size="large" onClick={handleMoveToDraft}>
                                Create as Position
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </StyledContainer>
    );
};

export default PositionDetails;
